import { createContext, useContext, useMemo, type ReactNode } from "react";

export type Direction = "ltr" | "rtl";

export interface LocaleContextValue {
  locale: string;
  currency: string;
  direction: Direction;
}

const RTL_LANGS = new Set(["ar", "he", "fa", "ur", "ps", "sd", "ug", "yi", "dv"]);

export function directionForLocale(locale: string): Direction {
  return RTL_LANGS.has(locale.split("-")[0] ?? "") ? "rtl" : "ltr";
}

const LocaleContext = createContext<LocaleContextValue>({
  locale: "en-US",
  currency: "USD",
  direction: "ltr",
});

export function LocaleProvider({
  locale = "en-US",
  currency = "USD",
  direction,
  children,
}: {
  locale?: string;
  currency?: string;
  direction?: Direction;
  children: ReactNode;
}) {
  const value = useMemo<LocaleContextValue>(
    () => ({ locale, currency, direction: direction ?? directionForLocale(locale) }),
    [locale, currency, direction],
  );
  // `display: contents` carries `dir` to descendants (for RTL) without adding a
  // layout box. Number/date formatting reads the locale from context directly.
  return (
    <LocaleContext.Provider value={value}>
      <div dir={value.direction} className="contents">
        {children}
      </div>
    </LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  return useContext(LocaleContext);
}
